<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Loader2, Mail, Lock, ArrowRight, ShieldCheck, Zap, BarChart3 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
const router = useRouter()
const authStore = useAuthStore()

const loginFormRef = ref<HTMLFormElement>()

const email = ref('')
const password = ref('')
const errorMessage = ref('')
const touched = reactive({ email: false, password: false })

const emailError = computed(() => {
  if (!touched.email) return ''
  if (!email.value) return 'El email es obligatorio'
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email.value) ? '' : 'Email no válido'
})

const passwordError = computed(() => {
  if (!touched.password) return ''
  if (!password.value) return 'La contraseña es obligatoria'
  return password.value.length < 6 ? 'Mínimo 6 caracteres' : ''
})

const isValid = computed(() => !emailError.value && !passwordError.value && email.value && password.value)

const handleLogin = async () => {
  touched.email = true
  touched.password = true
  if (!isValid.value) {
    errorMessage.value = 'Revisa los campos del formulario'
    return
  }
  errorMessage.value = ''
  await authStore.login(email.value, password.value)
}

const loginWithOAuth = (provider: string) => {
  authStore.loginWithOAuth(provider)
}

const goForgot = () => router.push('/forgot-password')
</script>

<template>
  <div class="min-h-screen grid md:grid-cols-2 lg:grid-cols-[5fr_7fr] bg-background">
    <!-- Panel branding -->
    <aside
      class="relative hidden md:flex flex-col justify-between p-6 md:p-8 lg:p-12 xl:p-16 text-white overflow-hidden bg-gradient-to-br from-[var(--dodger-blue)] via-[var(--azure-blue)] to-[var(--cobalt-blue)]"
    >
      <!-- Overlay oscuro para garantizar contraste del texto blanco sobre las zonas claras del degradado -->
      <div class="absolute inset-0 bg-gradient-to-br from-black/55 via-black/30 to-black/45" />

      <div class="absolute inset-0 opacity-10 [background-image:radial-gradient(circle_at_20%_20%,white_1px,transparent_1px)] [background-size:32px_32px]" />
      <div class="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
      <div class="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-[var(--neon-pink)]/10 blur-3xl" />

      <div class="relative z-10 flex items-center gap-3">
        <img src="@/assets/combined_logo.png" alt="CTI CRM" class="h-10 md:h-11 lg:h-12 w-auto" />
      </div>

      <div class="relative z-10 space-y-4 md:space-y-6">
        <h1 class="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight tracking-tight drop-shadow-[0_1px_4px_rgba(0,0,0,0.45)]">
          Centro de Contacto<br />Inteligente
        </h1>
        <p class="text-base md:text-lg text-white/90 max-w-sm md:max-w-md drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
          Gestiona leads, campañas y operadores en tiempo real desde una única plataforma.
        </p>
        <ul class="space-y-2 md:space-y-3 text-white/95">
          <li class="flex items-center gap-3">
            <span class="grid h-8 w-8 md:h-9 md:w-9 place-items-center rounded-full bg-white/15 shrink-0">
              <Zap class="h-4 w-4" />
            </span>
            <span class="text-sm md:text-base">Dashboard en tiempo real</span>
          </li>
          <li class="flex items-center gap-3">
            <span class="grid h-8 w-8 md:h-9 md:w-9 place-items-center rounded-full bg-white/15 shrink-0">
              <BarChart3 class="h-4 w-4" />
            </span>
            <span class="text-sm md:text-base">Analítica avanzada de conversión</span>
          </li>
          <li class="flex items-center gap-3">
            <span class="grid h-8 w-8 md:h-9 md:w-9 place-items-center rounded-full bg-white/15 shrink-0">
              <ShieldCheck class="h-4 w-4" />
            </span>
            <span class="text-sm md:text-base">Roles y permisos por equipo</span>
          </li>
        </ul>
      </div>

      <p class="relative z-10 text-xs md:text-sm text-white/60">Data Lean Makers, S.L. © {{ new Date().getFullYear() }}</p>
    </aside>

    <!-- Panel formulario -->
    <main class="flex items-center justify-center p-4 sm:p-6 md:p-10 lg:p-12">
      <div class="w-full max-w-sm space-y-6 md:max-w-md">
        <div class="md:hidden flex justify-center">
          <img src="@/assets/combined_logo.png" alt="CTI CRM" class="h-12 w-auto" />
        </div>

        <header class="space-y-1.5">
          <h2 class="text-xl sm:text-2xl font-semibold tracking-tight">Bienvenido de nuevo</h2>
          <p class="text-sm text-muted-foreground">Inicia sesión para acceder a tu panel</p>
        </header>

        <form ref="loginFormRef" class="space-y-4" data-tour="login-form" @submit.prevent="handleLogin">
          <div class="space-y-2">
            <Label for="email">Email</Label>
            <div class="relative">
              <Mail class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                v-model="email"
                type="email"
                autocomplete="email"
                name="email"
                placeholder="tu@email.com"
                class="pl-9"
                :aria-invalid="!!emailError"
                :aria-describedby="emailError ? 'email-error' : undefined"
                @blur="touched.email = true"
              />
            </div>
            <p v-if="emailError" id="email-error" class="text-xs text-destructive">{{ emailError }}</p>
          </div>

          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <Label for="password">Contraseña</Label>
              <button
                type="button"
                class="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                @click="goForgot"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            <div class="relative">
              <Lock class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                v-model="password"
                type="password"
                autocomplete="current-password"
                name="password"
                placeholder="Tu contraseña"
                class="pl-9"
                :aria-invalid="!!passwordError"
                :aria-describedby="passwordError ? 'password-error' : undefined"
                @blur="touched.password = true"
              />
            </div>
            <p v-if="passwordError" id="password-error" class="text-xs text-destructive">{{ passwordError }}</p>
          </div>

          <p v-if="errorMessage" role="alert" class="text-sm text-destructive">{{ errorMessage }}</p>

          <Button type="submit" class="w-full gap-2" :disabled="authStore.loading">
            <Loader2 v-if="authStore.loading" class="h-4 w-4 animate-spin" />
            <ArrowRight v-else class="h-4 w-4" />
            <span>Iniciar sesión</span>
          </Button>
        </form>

        <div class="relative">
          <Separator />
          <span class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-xs text-muted-foreground">
            o continúa con
          </span>
        </div>

        <div class="grid grid-cols-3 gap-3">
          <Button variant="outline" class="gap-2" aria-label="Iniciar sesión con Google" @click="loginWithOAuth('google')">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" class="h-4 w-4" />
            <span class="hidden sm:inline">Google</span>
          </Button>
          <Button variant="outline" class="gap-2" aria-label="Iniciar sesión con Facebook" @click="loginWithOAuth('facebook')">
            <img src="https://www.svgrepo.com/show/475647/facebook-color.svg" alt="Facebook" class="h-4 w-4" />
            <span class="hidden sm:inline">Facebook</span>
          </Button>
          <Button variant="outline" class="gap-2" aria-label="Iniciar sesión con Microsoft" @click="loginWithOAuth('microsoft')">
            <img src="https://www.svgrepo.com/show/448239/microsoft.svg" alt="Microsoft" class="h-4 w-4" />
            <span class="hidden sm:inline">Microsoft</span>
          </Button>
        </div>
      </div>
    </main>
  </div>

</template>
